import { useCallback, useEffect, useState } from "react";
import { Loader2, Pencil, Store, Save } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import {
    Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { toast } from "sonner";
import { OPS } from "@/constants/testIds";

/**
 * Quality → Branches: rename ONLY.
 *
 * The QC manager owns the branch labels used across the Quality module, so
 * they can correct a name here without holding infrastructure admin. Nothing
 * else about a branch is reachable from this screen — the POS id, the HR
 * branch mapping and the branch's identity are untouched, and the backend
 * enforces the same (PATCH /api/branches/{id}/name accepts names only).
 */
export default function QCBranchNamesView() {
    const { t, lang } = useI18n();
    const [branches, setBranches] = useState(null);
    const [editing, setEditing] = useState(null);   // branch object
    const [form, setForm] = useState({ name_ar: "", name_en: "" });
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const r = await api.get("/branches", { params: { include_inactive: true } });
            setBranches(r.data || []);
        } catch (e) {
            setBranches([]);
            toast.error(e?.response?.data?.detail || t("error_generic"));
        }
    }, [t]);

    useEffect(() => { load(); }, [load]);

    const openEdit = (b) => {
        setForm({ name_ar: b.name_ar || "", name_en: b.name_en || "" });
        setEditing(b);
    };

    const save = async () => {
        if (!editing || busy) return;
        const nameAr = (form.name_ar || "").trim();
        if (!nameAr) { toast.error(t("qc_branch_name_required")); return; }
        setBusy(true);
        try {
            await api.patch(`/branches/${editing.id}/name`, {
                name_ar: nameAr,
                name_en: (form.name_en || "").trim(),
            });
            toast.success(t("qc_branch_renamed"));
            setEditing(null);
            await load();
        } catch (e) {
            toast.error(e?.response?.data?.detail || t("error_generic"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <div data-testid={OPS.qcIns.branches.page} className="space-y-4">
            <div>
                <h2 className="text-base font-bold text-[color:var(--ink)]">{t("qc_branches_title")}</h2>
                <p className="text-xs text-[color:var(--ink-muted)]">{t("qc_branches_desc")}</p>
            </div>

            {branches === null ? (
                <Skeleton className="h-40 w-full" />
            ) : branches.length === 0 ? (
                <div className="rounded-lg border border-dashed border-[color:var(--border-soft)] py-8 text-center text-xs text-[color:var(--ink-muted)]">
                    {t("qc_branches_empty")}
                </div>
            ) : (
                <div className="rounded-xl border border-[color:var(--border-soft)] overflow-hidden">
                    <Table>
                        <TableHeader>
                            <TableRow>
                                <TableHead>{t("qc_branch_name_ar")}</TableHead>
                                <TableHead className="hidden sm:table-cell">{t("qc_branch_name_en")}</TableHead>
                                <TableHead className="w-[1%]" />
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {branches.map((b) => (
                                <TableRow key={b.id} data-testid={OPS.qcIns.branches.row(b.id)}>
                                    <TableCell className="font-semibold text-[color:var(--ink)]">
                                        <span className="inline-flex items-center gap-2">
                                            <Store className="h-3.5 w-3.5 text-[color:var(--brand-brown)]" />
                                            <bdi>{b.name_ar}</bdi>
                                        </span>
                                    </TableCell>
                                    <TableCell className="hidden sm:table-cell text-[color:var(--ink-muted)]">
                                        <bdi>{b.name_en || "—"}</bdi>
                                    </TableCell>
                                    <TableCell>
                                        <Button
                                            variant="outline"
                                            size="sm"
                                            data-testid={OPS.qcIns.branches.renameBtn(b.id)}
                                            onClick={() => openEdit(b)}
                                            className="gap-1.5"
                                        >
                                            <Pencil className="h-3.5 w-3.5" />
                                            {t("qc_branch_rename")}
                                        </Button>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            )}

            <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
                <DialogContent data-testid={OPS.qcIns.branches.dialog}>
                    <DialogHeader>
                        <DialogTitle>{t("qc_branch_rename_title")}</DialogTitle>
                        <DialogDescription>{t("qc_branch_rename_desc")}</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-3">
                        <div className="space-y-1.5">
                            <Label className="text-xs">{t("qc_branch_name_ar")}</Label>
                            <Input
                                data-testid={OPS.qcIns.branches.inputAr}
                                value={form.name_ar}
                                onChange={(e) => setForm((f) => ({ ...f, name_ar: e.target.value }))}
                                dir="rtl"
                            />
                        </div>
                        <div className="space-y-1.5">
                            <Label className="text-xs">{t("qc_branch_name_en")}</Label>
                            <Input
                                data-testid={OPS.qcIns.branches.inputEn}
                                value={form.name_en}
                                onChange={(e) => setForm((f) => ({ ...f, name_en: e.target.value }))}
                                dir="ltr"
                            />
                        </div>
                        <p className="text-[10px] leading-snug text-[color:var(--ink-muted)]">
                            {t("qc_branch_rename_hint")}
                        </p>
                    </div>
                    <DialogFooter>
                        <Button variant="ghost" onClick={() => setEditing(null)} disabled={busy}
                            data-testid={OPS.qcIns.branches.cancel}>
                            {t("cancel")}
                        </Button>
                        <Button onClick={save} disabled={busy} data-testid={OPS.qcIns.branches.save} className="gap-1.5">
                            {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
                            {t("save")}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
